import { useFindCrItems } from '@/components/CrItemsSelect/Provider';
import { useFindCrSubItems } from '@/components/CrSubItemsSelect/Provider';
import { useFindCurrencies } from '@/components/CurrenciesSelect/Provider';
import { useFindDepts } from '@/components/DeptSelect/Provider';
import { useFindExcise } from '@/components/ExciseSelect/Provider';
import { useFindExItems } from '@/components/ExItemSelect/Provider';
import {
  useFindProjectCodes,
  useFindProjects,
} from '@/components/ProjectSelect/Provider';
import { useGetCrItemsAllSuspense } from '@/context/services/cr_items/CrItems.service';
import { useGetCrSubItemsAllSuspense } from '@/context/services/cr_sub_items/CrSubItems.service';
import { useGetCurrencyUnitsAllSuspense } from '@/context/services/currencies/Currencies.service';
import { useGetDeptsAllSuspense } from '@/context/services/dept/Dept.service';
import { useGetExItemsAllSuspense } from '@/context/services/ex_item/ExItem.service';
import { useGetExcisesAllSuspense } from '@/context/services/excise/Excise.service';
import { invoiceKindEnumToI18nLabel } from '@/context/services/invoice_kind/InvoiceKind.service';
import { useTranslation } from '@/i18n';
import { useGetProjectCodesAllSuspense } from '@/context/services/project_code/ProjectCode.service';
import { useAggregatedCustomPulldownItems } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Components/CustomTransactionItems/PulldownType/useAggregatedCustomPulldownItems';
import { mapCustomItemLabelForExport } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/hooks/useGetCustomItemMapping';

import { exportMappings } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/importExportMapping';
import { formatDateForExport } from '@/features/InvoiceReport/Edit/components/utils/customTransactionItems';
import {
  InvoiceTransaction,
  TransactionProperty,
} from '@/features/InvoiceReport/Edit/type';
import {
  exportsUrl,
  exportsWorkBook,
} from '@moneyforward/csv-excel-imp-exp-util';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { toISODateString } from 'date-util';
import { useCallback, useMemo } from 'react';

export type ExportableCommonValue = {
  dealDate: string;
  name: string;
  jpyRate: string;
  quantity: string;
  withholdingIncomeTax: string;
  memo: string;
  currency: string;
};

export type InvoiceTransactionAdditionalValue = {
  exItemName: string;
  exciseName: string;
  deptName: string;
  projectName: string;
  crItemName: string;
  crSubItemName: string;
  tax: string; // correspond to exciseValue
  unitPriceExcludingTax: string; // correspond to unit
  unitPriceIncludingTax: string; // correspond to taxIncludedUnit
  totalValueExcludingTax: string; // correspond to totalValue
  totalValueIncludingTax: string; // correspond to taxIncludedTotalValue
  invoiceKind: string;
};

export type InvoiceTransactionCustomValue = {
  [key: string]: string;
};

export type ExportableInvoiceTransactionType = ExportableCommonValue &
  InvoiceTransactionAdditionalValue &
  InvoiceTransactionCustomValue;

const exportableCommonValue: ExportableCommonValue = {
  dealDate: '',
  name: '',
  quantity: '',
  withholdingIncomeTax: '',
  memo: '',
  currency: '',
  jpyRate: '',
};

const additionalValue: InvoiceTransactionAdditionalValue = {
  exItemName: '',
  exciseName: '',
  deptName: '',
  projectName: '',
  crItemName: '',
  crSubItemName: '',
  tax: '',
  unitPriceExcludingTax: '',
  unitPriceIncludingTax: '',
  totalValueExcludingTax: '',
  totalValueIncludingTax: '',
  invoiceKind: '',
};

export const exportableDefaultValue: ExportableInvoiceTransactionType =
  Object.assign({}, exportableCommonValue, additionalValue);

const useConvertFieldsToExportData = (
  transactionProperties: TransactionProperty[] = []
) => {
  const isInvoiceKindUnqualifiedSeventyFlag = useBooleanFlagValue(
    'invoice-kind-unqualified-seventy',
    false
  );
  const { t } = useTranslation();

  const { findExciseById } = useFindExcise();
  const { findDeptById } = useFindDepts();
  const { findCrItemsById } = useFindCrItems();
  const { findCrSubItemsById } = useFindCrSubItems();
  const { findExItemsById } = useFindExItems();
  const { findProjectCodesById } = useFindProjectCodes();
  const { findProjectsById } = useFindProjects();
  const { findCurrenciesById } = useFindCurrencies();

  const useProjectSelectById = useBooleanFlagValue(
    'use-project-select-for-report-form',
    false
  );

  const { findCustomPulldownOptionById } = useAggregatedCustomPulldownItems(
    transactionProperties
  );

  return useCallback(
    (
      items: InvoiceTransaction[],
      isEmpty?: boolean
    ): ExportableInvoiceTransactionType[] => {
      if (isEmpty) {
        return [exportableDefaultValue];
      }

      return items.map((item) => {
        const excise = findExciseById(item.drExciseId);
        const dept = findDeptById(item.deptId);
        const crItem = findCrItemsById(item.crItemId);
        const crSubItem = findCrSubItemsById(item.crSubItemId);
        const project = useProjectSelectById
          ? findProjectsById(item.projectId)
          : findProjectCodesById(item.projectCodeId);
        const currency = findCurrenciesById(item.currency);
        const exItemName = findExItemsById(item.exItemId);

        const customizableProperties = transactionProperties.filter(
          (prop) => prop.type === 'custom'
        );

        const baseData: ExportableInvoiceTransactionType = {
          name: item.name ?? '',
          dealDate: item.dealDate ? toISODateString(item.dealDate) : '',
          exItemName: exItemName ?? '',
          quantity: item.quantity?.toString() ?? '',
          unitPriceExcludingTax: item.unit?.toString() ?? '',
          unitPriceIncludingTax: item.taxIncludedUnit.toString() ?? '',
          totalValueExcludingTax: item.totalValue?.toString() ?? '',
          totalValueIncludingTax: item.taxIncludedTotalValue.toString() ?? '',
          exciseName: excise ?? '',
          tax: item.exciseValue?.toString() ?? '',
          withholdingIncomeTax: item.withholdingIncomeTax?.toString() ?? '',
          deptName: dept ?? '',
          projectName: project ?? '',
          crItemName: crItem ?? '',
          crSubItemName: crSubItem ?? '',
          memo: item.memo ?? '',
          currency: currency ?? '',
          jpyRate: item.jpyRate?.toString() ?? '',
          invoiceKind: isInvoiceKindUnqualifiedSeventyFlag
            ? invoiceKindEnumToI18nLabel(t, item.invoiceKind)
            : item.invoiceKind ?? '',
        };

        // Add custom transaction items
        const customItems: Record<string, string> = {};
        customizableProperties.forEach((customProperty) => {
          const key = mapCustomItemLabelForExport(customProperty.label);
          let value: string = '';

          if (customProperty.componentType === 'pulldown') {
            value =
              findCustomPulldownOptionById(
                customProperty.id,
                item.custom_transaction_items?.[customProperty.id]
              ) ?? '';
          } else if (customProperty.componentType === 'date') {
            value =
              formatDateForExport(
                item.custom_transaction_items?.[customProperty.id],
                customProperty.dateFormatType
              ) ?? '';
          } else {
            value = String(
              item.custom_transaction_items?.[customProperty.id] ?? ''
            );
          }

          customItems[key] = value;
        });

        return {
          ...baseData,
          ...customItems,
        } satisfies ExportableInvoiceTransactionType;
      });
    },
    [
      findCrItemsById,
      findCrSubItemsById,
      findCurrenciesById,
      findDeptById,
      findExItemsById,
      findExciseById,
      findProjectCodesById,
      findProjectsById,
      useProjectSelectById,
      transactionProperties,
      findCustomPulldownOptionById,
      isInvoiceKindUnqualifiedSeventyFlag,
      t,
    ]
  );
};

const useExportsCsv = (
  taxInclude: boolean,
  invoiceTransactionDetails: boolean,
  isAdminOrApproval: boolean,
  hasWithholdingIncomeTax: boolean,
  canDisplayDrExcise: boolean,
  transactionProperties: TransactionProperty[] = []
) => {
  return useCallback(
    async (
      fields: (InvoiceTransaction | ExportableInvoiceTransactionType)[],
      encoding: 'utf-8' | 'sjis'
    ) => {
      const url = await exportsUrl({
        data: fields,
        mappingObject: exportMappings(
          isAdminOrApproval,
          invoiceTransactionDetails,
          taxInclude,
          hasWithholdingIncomeTax,
          canDisplayDrExcise,
          transactionProperties
        ),
        format: 'csv',
        encodeType: encoding,
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = '支払依頼.csv';
      a.click();
      a.remove();
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    },
    [
      hasWithholdingIncomeTax,
      invoiceTransactionDetails,
      isAdminOrApproval,
      taxInclude,
      canDisplayDrExcise,
      transactionProperties,
    ]
  );
};

const useExportExcel = (
  taxInclude: boolean,
  invoiceTransactionDetails: boolean,
  isAdminOrApproval: boolean,
  hasWithholdingIncomeTax: boolean,
  canDisplayDrExcise: boolean,
  transactionProperties: TransactionProperty[] = []
) => {
  const exportDropdown = useExportDropdownData(transactionProperties);
  return useCallback(
    async (
      fields: (InvoiceTransaction | ExportableInvoiceTransactionType)[]
    ) => {
      const mapping = exportMappings(
        isAdminOrApproval,
        invoiceTransactionDetails,
        taxInclude,
        hasWithholdingIncomeTax,
        canDisplayDrExcise,
        transactionProperties
      );
      const wb = exportsWorkBook(fields, mapping);
      exportDropdown(wb, mapping, canDisplayDrExcise);
      const url = await exportsUrl({
        data: wb,
        format: 'xlsx',
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = '支払依頼.xlsx';
      a.click();
      a.remove();
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    },
    [
      isAdminOrApproval,
      invoiceTransactionDetails,
      taxInclude,
      hasWithholdingIncomeTax,
      canDisplayDrExcise,
      transactionProperties,

      exportDropdown,
    ]
  );
};

type BaseExportArgs = {
  taxInclude: boolean;
  invoiceTransactionDetails: boolean;
  isAdminOrApproval: boolean;
  hasWithholdingIncomeTax: boolean;
  canDisplayDrExcise: boolean;
  transactionProperties?: TransactionProperty[];
};

type ExportArgs = {
  fields: InvoiceTransaction[];
} & BaseExportArgs;

export const useExportXlsx = ({
  fields,
  taxInclude,
  invoiceTransactionDetails,
  isAdminOrApproval,
  hasWithholdingIncomeTax,
  canDisplayDrExcise,
  transactionProperties = [],
}: ExportArgs) => {
  const convertFieldsToExportData = useConvertFieldsToExportData(
    transactionProperties
  );
  const onWrapExportsXlsx = useExportExcel(
    taxInclude,
    invoiceTransactionDetails,
    isAdminOrApproval,
    hasWithholdingIncomeTax,
    canDisplayDrExcise,
    transactionProperties
  );

  const onExportsXlsx = useCallback(
    async () =>
      await onWrapExportsXlsx(await convertFieldsToExportData(fields)),
    [convertFieldsToExportData, fields, onWrapExportsXlsx]
  );

  return {
    onExportsXlsx,
  };
};

export const useExportHeader = ({
  taxInclude,
  invoiceTransactionDetails,
  isAdminOrApproval,
  hasWithholdingIncomeTax,
  canDisplayDrExcise,
  transactionProperties = [],
}: BaseExportArgs) => {
  const customizableProperties = useMemo(
    () => transactionProperties.filter((prop) => prop.type === 'custom'),
    [transactionProperties]
  );

  const headerValue = useMemo(() => {
    const value = { ...exportableDefaultValue };
    customizableProperties.forEach((prop) => {
      const key = mapCustomItemLabelForExport(prop.label);
      value[key] = '';
    });
    return value;
  }, [customizableProperties]);

  const onWrapExportsXlsx = useExportExcel(
    taxInclude,
    invoiceTransactionDetails,
    isAdminOrApproval,
    hasWithholdingIncomeTax,
    canDisplayDrExcise,
    transactionProperties
  );
  const onExportsXlsxWithOnlyHeader = useCallback(
    async () => await onWrapExportsXlsx([headerValue]),
    [onWrapExportsXlsx, headerValue]
  );

  const exportsCsv = useExportsCsv(
    taxInclude,
    invoiceTransactionDetails,
    isAdminOrApproval,
    hasWithholdingIncomeTax,
    canDisplayDrExcise,
    transactionProperties
  );

  const onExportsUtf8CsvOnlyHeader = useCallback(async () => {
    await exportsCsv([headerValue], 'utf-8');
  }, [exportsCsv, headerValue]);
  const onExportsSjisCsvOnlyHeader = useCallback(async () => {
    await exportsCsv([headerValue], 'sjis');
  }, [exportsCsv, headerValue]);

  return {
    onExportsUtf8CsvOnlyHeader,
    onExportsSjisCsvOnlyHeader,
    onExportsXlsxWithOnlyHeader,
  };
};

export const useExportCsv = ({
  fields,
  taxInclude,
  invoiceTransactionDetails,
  isAdminOrApproval,
  hasWithholdingIncomeTax,
  canDisplayDrExcise,
  transactionProperties = [],
}: ExportArgs) => {
  const convertFieldsToExportData = useConvertFieldsToExportData(
    transactionProperties
  );
  const exportsCsv = useExportsCsv(
    taxInclude,
    invoiceTransactionDetails,
    isAdminOrApproval,
    hasWithholdingIncomeTax,
    canDisplayDrExcise,
    transactionProperties
  );
  const onExportsCsv = useCallback(
    async (encoding: 'utf-8' | 'sjis') => {
      await exportsCsv(await convertFieldsToExportData(fields), encoding);
    },
    [exportsCsv, convertFieldsToExportData, fields]
  );
  const onExportsUtf8Csv = useCallback(async () => {
    onExportsCsv('utf-8');
  }, [onExportsCsv]);

  // NOTE: sjis export
  const onExportsSjisCsv = useCallback(async () => {
    onExportsCsv('sjis');
  }, [onExportsCsv]);

  return {
    onExportsUtf8Csv,
    onExportsSjisCsv,
  };
};

const indexToExcelColumn = (index: number): string => {
  let column = '';
  // 内部計算のため1始まりに変換
  index++;

  while (index > 0) {
    // 26進数のように計算（ただしExcelは1から始まる）
    const remainder = (index - 1) % 26;
    column = String.fromCharCode(65 + remainder) + column;
    index = Math.floor((index - 1) / 26);
  }

  return column;
};

const useExportDropdownData = (
  transactionProperties: TransactionProperty[] = []
) => {
  const exItems = useGetExItemsAllSuspense();
  const excisesItems = useGetExcisesAllSuspense();
  const deptItems = useGetDeptsAllSuspense();
  const prjItem = useGetProjectCodesAllSuspense();
  const crItems = useGetCrItemsAllSuspense();
  const crSubItems = useGetCrSubItemsAllSuspense();
  const currencyItems = useGetCurrencyUnitsAllSuspense();

  const { aggregatedCustomPulldownItems } = useAggregatedCustomPulldownItems(
    transactionProperties
  );

  return useCallback(
    (
      wb: ReturnType<typeof exportsWorkBook>,
      mapping: Record<string, string>,
      canDisplayDrExcise: boolean
    ) => {
      const mappingKeys = Object.keys(mapping);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items: (Record<string, any> & { name?: string; cell: string })[] = [
        { exItems, name: 'exItemName', cell: 'C' },
        { excisesItems, name: 'exciseName', cell: 'I' },
        { deptItems, name: 'deptName', cell: 'M' },
        { prjItem, name: 'projectName', cell: 'N' },
        { crItems, name: 'crItemName', cell: 'O' },
        { crSubItems, name: 'crSubItemName', cell: 'P' },
        { currencyItems, name: 'currency', cell: 'Q' },
      ].filter((item) => canDisplayDrExcise || item.name !== 'exciseName');

      aggregatedCustomPulldownItems
        .filter((item) => !!item.id)
        .forEach((item, index) => {
          const columnName = mapCustomItemLabelForExport(item.label);
          const sheetName = `Custom_${index}`;
          const options = item.options?.map(({ display_name }) => ({
            display_name,
          }));

          items.push({
            [sheetName]: options,
            name: columnName,
            cell: '',
          });
        });

      items.forEach((item) => {
        const idx = mappingKeys.findIndex((key) => key === item.name);
        const cell = indexToExcelColumn(idx);
        item.cell = cell;
        delete item.name;
      });

      items.forEach((item) => {
        let sheetName: string = '';
        let values: unknown[] = [];
        let cell: string = '';
        Object.entries(item).forEach(([key, value]) => {
          if (key === 'cell') {
            cell = value;
          } else {
            sheetName = key;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            values = value.map((value: any) => ({
              // selectで出力しているのは基本 display_name なので display_name を最初にする
              name: value.display_name ?? value.long_name ?? value.name,
            }));
          }
        });
        const sh = wb.addWorksheet(sheetName);
        sh.columns = [
          {
            header: '名前',
            key: 'name',
          },
        ];
        sh.addRows(values);
        sh.state = 'veryHidden';
        //@ts-ignore
        wb.getWorksheet('sheet1')?.dataValidations.add(`${cell}2:${cell}9999`, {
          type: 'list',
          allowBlank: true,
          formulae: [`'${sheetName}'!$A$2:$A$${values.length + 1}`],
        });
      });
    },
    [
      crItems,
      crSubItems,
      currencyItems,
      deptItems,
      exItems,
      excisesItems,
      prjItem,
      aggregatedCustomPulldownItems,
    ]
  );
};
